// threads.ts
//------------------------------------------------------------
// A tiny registry that tracks a request/response “thread”
// across the lifetime of a WebSocket round-trip.
//------------------------------------------------------------

type Params = Record<string, any>; // user-supplied request metadata
type Values = Record<string, any>; // arbitrary mutable store
type ThreadEntry = {
  promise: Promise<any>; // resolves when backend replies
  resolve: (v: any) => void; // fulfiller kept internally
  reject: (e?: any) => void;
  params: Params;
  values: Values;
};

const threads = new Map<string, ThreadEntry>();

/** Create a new thread and return its promise (internal use). */
export function createThread(threadId: string, params: Params = {}): Promise<any> {
  if (threads.has(threadId)) {
    throw new Error(`Thread “${threadId}” already exists`);
  }

  let resolve!: (v: any) => void;
  let reject!: (e?: any) => void;

  const promise = new Promise<any>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  threads.set(threadId, { promise, resolve, reject, params, values: {} });
  return promise;
}

/** Resolve a thread’s promise and clean up (internal use). */
export function finishThread(threadId: string, payload: any, isError = false) {
  const thread = threads.get(threadId);
  if (!thread) return; // silently ignore stray frames

  isError ? thread.reject(payload) : thread.resolve(payload);
  threads.delete(threadId);
}

// -----------------------------------------------------------
// Public helpers (the ones you asked for)
// -----------------------------------------------------------

/** Get the promise that will resolve when the backend replies. */
export const getThreadPromise = (threadId: string) => {
  const t = threads.get(threadId);
  if (!t) throw new Error(`Unknown thread “${threadId}”`);
  return t.promise;
};

/** Retrieve the immutable params object recorded at creation. */
export const getThreadParams = (threadId: string) => {
  const t = threads.get(threadId);
  if (!t) throw new Error(`Unknown thread “${threadId}”`);
  return t.params;
};

/** Stash a mutable value on the thread (e.g. progress, chunks, etc.). */
export const setThreadValue = (threadId: string, key: string, value: any) => {
  const t = threads.get(threadId);
  if (!t) throw new Error(`Unknown thread “${threadId}”`);
  t.values[key] = value;
};

/** Read a previously stored mutable value. */
export const getThreadValue = (threadId: string, key: string) => {
  const t = threads.get(threadId);
  if (!t) throw new Error(`Unknown thread “${threadId}”`);
  return t.values[key];
};

export const hasThread = (threadId: string) => {
  return threads.has(threadId);
};
